import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Form, Alert, Spinner, Card, Badge } from "react-bootstrap";
import axios from "axios";
import {
    FaPlus, FaSyringe, FaThermometerHalf, FaBell,
    FaMoneyBillWave, FaTimes, FaCheck, FaEdit, FaTrash
} from "react-icons/fa";
import Select from "react-select";
import moment from "moment";
import Swal from "sweetalert2";

export default function PetVaccineComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const API = process.env.REACT_APP_API_URL;
    const AuthToken = localStorage.getItem("authToken");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const role = localStorage.getItem("role") || "";
    const hasPermission = (perm) => permissions.includes(perm);
    const isAdmin = role === "Admin";

    const [groups, setGroups] = useState([]);
    const [vaccineOptions, setVaccineOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [completeState, setCompleteState] = useState({});
    const [addShotState, setAddShotState] = useState({});
    const [updateState, setUpdateState] = useState({});

    const showToast = (icon, title) => {
        Swal.mixin({
            toast: true, position: "top-end", showConfirmButton: false,
            timer: 3000, timerProgressBar: true,
        }).fire({ icon, title });
    };

    useEffect(() => {
        fetchData();
    }, [appointmentId, petId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/pet-vaccines/${petId}`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            const list = response.data?.petVaccines || [];
            const products = response.data?.vaccine || [];

            const options = products.map(p => ({
                value: p.id,
                label: p.product_name,
                stock: p.stock ?? p.quantity ?? 0,
                sale_price: p.sale_price,
                purchase_price: p.purchase_price,
            }));

            setVaccineOptions(options);
            setGroups(list);
        } catch (error) {
            console.error(error);
            showToast("error", t("failed to load vaccines"));
        } finally {
            setLoading(false);
        }
    };

    const findVaccine = (id, fallbackName = "") => {
        let found = vaccineOptions.find(v => v.value === id);
        if (!found && fallbackName) {
            found = vaccineOptions.find(v => v.label.toLowerCase() === fallbackName.toLowerCase());
        }
        return found;
    };

    const formatOptionLabel = (option) => (
        <div className="d-flex justify-content-between align-items-center w-100">
            <span className="fw-medium">{option.label}</span>
            <span className="small text-muted ms-2" style={{ fontSize: "0.8rem" }}>
                Stock: <strong className="text-dark me-2">{option.stock ?? 0}</strong>
                {option.sale_price !== undefined && option.sale_price !== null && (
                    <>Price: <strong className="text-success">{option.sale_price}</strong></>
                )}
            </span>
        </div>
    );

    const hasPendingVaccines = groups.some(g => g.status === "reminder");
    const showInlineAddButton = !hasPendingVaccines && isAppointmentOpen && hasPermission("Appointment Vaccine Create") && !addShotState.open;

    const handleDelete = async (groupId) => {
        const result = await Swal.fire({
            title: t("Are you sure?"),
            text: t("You won't be able to revert this vaccine record!"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t("Yes, delete it!"),
            cancelButtonText: t("Cancel")
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await axios.delete(`${API}/pet-vaccines/${groupId}`, {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                });
                showToast("success", t("Vaccine record deleted successfully."));
                fetchData();
            } catch (error) {
                console.error(error);
                showToast("error", error.response?.data?.message || t("failed to delete vaccine"));
                setLoading(false);
            }
        }
    };

    const openComplete = (group) => {
        setCompleteState(prev => ({
            ...prev,
            [group.group_id]: {
                open: true,
                temp: "",
                vaccine_date: group.vaccine_date ? moment(group.vaccine_date).format("YYYY-MM-DD") : "",
                rows: group.rows.map(r => {
                    const targetVaccine = findVaccine(r.vaccine_id, r.vaccine_name);
                    return {
                        vaccine_id: targetVaccine ? targetVaccine.value : r.vaccine_id,
                        vaccine_name: targetVaccine ? targetVaccine.label : r.vaccine_name,
                        price: r.price ?? (targetVaccine?.sale_price || ""),
                        quantity: r.quantity ?? 1,
                    };
                }),
                addReminder: false,
                reminder_date: "",
                reminders: [],
                submitting: false,
            },
        }));
    };

    const closeComplete = (groupId) => {
        setCompleteState(prev => {
            const { [groupId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const updateCompleteField = (groupId, field, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], [field]: value },
        }));
    };

    const updateCompleteRow = (groupId, vaccineId, field, value) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.map(r =>
                    r.vaccine_id === vaccineId ? { ...r, [field]: value } : r
                ),
            },
        }));
    };

    const updateCompleteVaccines = (groupId, selected) => {
        const selectedOptions = selected || [];
        setCompleteState(prev => {
            const current = prev[groupId];
            const rows = selectedOptions.map(opt => {
                const existing = current.rows.find(r => r.vaccine_id === opt.value);
                if (existing) return existing;
                return {
                    vaccine_id: opt.value,
                    vaccine_name: opt.label,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                };
            });
            return { ...prev, [groupId]: { ...current, rows } };
        });
    };

    const removeCompleteVaccine = (groupId, vaccineId) => {
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.filter(r => r.vaccine_id !== vaccineId),
            },
        }));
    };

    const updateCompleteReminders = (groupId, selected) => {
        const selectedOptions = selected || [];
        setCompleteState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                reminders: selectedOptions.map(opt => ({ vaccine_id: opt.value })),
            },
        }));
    };

    const submitComplete = async (group) => {
        const data = completeState[group.group_id];
        if (!data) return;

        if (data.addReminder && (!data.reminder_date || data.reminders.length === 0)) {
            showToast("warning", t("please select reminder vaccines and date"));
            return;
        }

        try {
            updateCompleteField(group.group_id, "submitting", true);

            const payload = {
                appointment_id: appointmentId || undefined,
                pet_id: petId,
                temp: data.temp || null,
                vaccine_date: data.vaccine_date || null,
                vaccines: data.rows.map(r => ({
                    vaccine_id: parseInt(r.vaccine_id),
                    price: r.price === "" ? null : parseFloat(r.price),
                    quantity: parseInt(r.quantity) || 1,
                })),
            };

            if (data.addReminder) {
                payload.reminder_date = data.reminder_date;
                payload.reminders = data.reminders.map(r => ({
                    vaccine_id: parseInt(r.vaccine_id)
                }));
            }

            await axios.post(`${API}/pet-vaccines/complete/${group.group_id}`, payload, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            closeComplete(group.group_id);
            fetchData();
            showToast("success", t("Vaccine completed successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed_to_complete_vaccine"));
            updateCompleteField(group.group_id, "submitting", false);
        }
    };

    const openUpdate = (group) => {
        if (group.status === "completed") {
            const rows = group.rows.map(r => {
                const targetVaccine = findVaccine(r.vaccine_id, r.vaccine_name);
                return {
                    vaccine_id: targetVaccine ? targetVaccine.value : r.vaccine_id,
                    vaccine_name: targetVaccine ? targetVaccine.label : r.vaccine_name,
                    price: r.price ?? (targetVaccine?.sale_price || ""),
                    quantity: r.quantity ?? 1,
                };
            });

            setUpdateState(prev => ({
                ...prev,
                [group.group_id]: {
                    open: true,
                    status: "completed",
                    temp: group.temp ?? "",
                    vaccine_date: group.vaccine_date ? moment(group.vaccine_date).format("YYYY-MM-DD") : "",
                    rows: rows,
                    addReminder: false,
                    reminder_date: "",
                    reminders: [],
                    submitting: false,
                },
            }));
        } else {
            const reminders = group.rows.map(r => {
                const targetVaccine = findVaccine(r.vaccine_id, r.vaccine_name);
                return { vaccine_id: targetVaccine ? targetVaccine.value : r.vaccine_id };
            });

            setUpdateState(prev => ({
                ...prev,
                [group.group_id]: {
                    open: true,
                    status: "reminder",
                    reminder_date: group.vaccine_date ? moment(group.vaccine_date).format("YYYY-MM-DD") : "",
                    reminders: reminders,
                    submitting: false,
                },
            }));
        }
    };

    const closeUpdate = (groupId) => {
        setUpdateState(prev => {
            const { [groupId]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const updateUpdateField = (groupId, field, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: { ...prev[groupId], [field]: value },
        }));
    };

    const updateUpdateRow = (groupId, vaccineId, field, value) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.map(r =>
                    r.vaccine_id === vaccineId ? { ...r, [field]: value } : r
                ),
            },
        }));
    };

    const updateUpdateVaccines = (groupId, selected) => {
        const selectedOptions = selected || [];
        setUpdateState(prev => {
            const current = prev[groupId];
            const rows = selectedOptions.map(opt => {
                const existing = current.rows.find(r => r.vaccine_id === opt.value);
                if (existing) return existing;
                return {
                    vaccine_id: opt.value,
                    vaccine_name: opt.label,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                };
            });
            return { ...prev, [groupId]: { ...current, rows } };
        });
    };

    const removeUpdateVaccine = (groupId, vaccineId) => {
        setUpdateState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                rows: prev[groupId].rows.filter(r => r.vaccine_id !== vaccineId),
            },
        }));
    };

    const updateUpdateReminderVaccines = (groupId, selected) => {
        const selectedOptions = selected || [];
        setUpdateState(prev => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                reminders: selectedOptions.map(opt => ({ vaccine_id: opt.value })),
            },
        }));
    };

    const submitUpdate = async (group) => {
        const data = updateState[group.group_id];
        if (!data) return;

        if (data.status === "completed") {
            if (data.rows.length === 0) {
                showToast("warning", t("please select at least one vaccine"));
                return;
            }
            if (data.addReminder && (!data.reminder_date || data.reminders.length === 0)) {
                showToast("warning", t("please select reminder vaccines and date"));
                return;
            }
        }

        if (data.status === "reminder" && (!data.reminder_date || data.reminders.length === 0)) {
            showToast("warning", t("please select reminder vaccines and date"));
            return;
        }

        try {
            updateUpdateField(group.group_id, "submitting", true);

            let payload;

            if (data.status === "completed") {
                payload = {
                    pet_id: petId,
                    appointment_id: appointmentId || undefined,
                    temp: data.temp === "" ? null : data.temp,
                    vaccine_date: data.vaccine_date || null,
                    vaccines: data.rows.map(r => ({
                        vaccine_id: parseInt(r.vaccine_id),
                        price: r.price === "" ? null : parseFloat(r.price),
                        quantity: parseInt(r.quantity) || 1,
                    })),
                };

                if (data.addReminder) {
                    payload.reminder_date = data.reminder_date;
                    payload.reminders = data.reminders.map(r => ({
                        vaccine_id: parseInt(r.vaccine_id)
                    }));
                }
            } else {
                payload = {
                    pet_id: petId,
                    reminder_date: data.reminder_date,
                    reminders: data.reminders.map(r => ({
                        vaccine_id: parseInt(r.vaccine_id),
                    })),
                };
            }

            await axios.post(`${API}/pet-vaccines/${group.group_id}`, payload, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            });

            closeUpdate(group.group_id);
            fetchData();
            showToast("success", t("Vaccine updated successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed to update vaccine"));
            updateUpdateField(group.group_id, "submitting", false);
        }
    };

    const openAddShot = () => {
        setAddShotState({
            open: true,
            vaccine_date: moment().format("YYYY-MM-DD"),
            vaccines: [],
            addReminder: false,
            reminder_date: "",
            reminders: [],
            temp: "",
            submitting: false,
        });
    };

    const closeAddShot = () => setAddShotState({});
    const updateAddShotStateField = (field, value) => setAddShotState(prev => ({ ...prev, [field]: value }));

    const updateAddShotVaccines = (selected) => {
        const selectedOptions = selected || [];
        setAddShotState(prev => {
            const rows = selectedOptions.map(opt => {
                const existing = prev.vaccines.find(v => v.vaccine_id === opt.value);
                if (existing) return existing;
                return {
                    vaccine_id: opt.value,
                    vaccine_name: opt.label,
                    price: opt.sale_price ?? "",
                    quantity: 1,
                };
            });
            return { ...prev, vaccines: rows };
        });
    };

    const updateAddShotRow = (vaccineId, field, value) => {
        setAddShotState(prev => ({
            ...prev,
            vaccines: prev.vaccines.map(v => v.vaccine_id === vaccineId ? { ...v, [field]: value } : v),
        }));
    };

    const removeAddShotVaccine = (vaccineId) => {
        setAddShotState(prev => ({ ...prev, vaccines: prev.vaccines.filter(v => v.vaccine_id !== vaccineId) }));
    };

    const updateAddShotReminders = (selected) => {
        const selectedOptions = selected || [];
        setAddShotState(prev => ({ ...prev, reminders: selectedOptions.map(opt => ({ vaccine_id: opt.value })) }));
    };

    const submitAddShot = async () => {
        if (addShotState.vaccines.length === 0) {
            showToast("warning", t("please select at least one vaccine"));
            return;
        }

        if (addShotState.addReminder && (!addShotState.reminder_date || addShotState.reminders.length === 0)) {
            showToast("warning", t("please select reminder vaccines and date"));
            return;
        }

        try {
            setAddShotState(prev => ({ ...prev, submitting: true }));
            const payload = {
                pet_id: petId,
                appointment_id: appointmentId || null,
                temp: addShotState.temp || null,
                vaccine_date: addShotState.vaccine_date,
                vaccines: addShotState.vaccines.map(v => ({
                    vaccine_id: parseInt(v.vaccine_id),
                    quantity: parseInt(v.quantity) || 1,
                    price: v.price === "" ? null : parseFloat(v.price),
                })),
            };

            if (addShotState.addReminder) {
                payload.reminder_date = addShotState.reminder_date;
                payload.reminders = addShotState.reminders.map(r => ({ vaccine_id: parseInt(r.vaccine_id) }));
            }

            await axios.post(`${API}/pet-vaccines`, payload, { headers: { Authorization: `Bearer ${AuthToken}` } });
            closeAddShot();
            fetchData();
            showToast("success", t("Vaccine entries saved successfully!"));
        } catch (error) {
            console.error(error);
            showToast("error", error.response?.data?.message || t("failed to save vaccine"));
            setAddShotState(prev => ({ ...prev, submitting: false }));
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5 my-4">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const selectStyles = {
        control: (base, state) => ({ ...base, borderColor: state.isFocused ? '#86b7fe' : '#dee2e6', borderRadius: '0.375rem', fontSize: '0.9rem' }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const hasMatchedVaccineAppointment = groups.some(g => parseInt(g.appointment_id) === parseInt(appointmentId));

    return (
        <div className="vaccine-container">
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-dark-emphasis fw-bold text-uppercase">{t("vaccines list")}</h6>
            </div>

            <Row xs={1} sm={2} lg={3} className="g-3">
                {groups.map((group) => {
                    const complete = completeState[group.group_id];
                    const upd = updateState[group.group_id];

                    const currentSelectedValues = complete?.open
                        ? vaccineOptions.filter(o =>
                            complete.rows.some(r => Number(r.vaccine_id) === Number(o.value))
                        )
                        : [];

                    const currentUpdateSelectedValues = (upd?.open && upd.status === "completed")
                        ? vaccineOptions.filter(o =>
                            upd.rows.some(r => Number(r.vaccine_id) === Number(o.value))
                        )
                        : [];

                    const currentUpdateReminderValues = (upd?.open && upd.status === "reminder")
                        ? vaccineOptions.filter(o => upd.reminders.some(r => r.vaccine_id === o.value))
                        : [];

                    const currentUpdateReminderSelectedValues = (upd?.open && upd.status === "completed" && upd.addReminder)
                        ? vaccineOptions.filter(o => upd.reminders.some(r => r.vaccine_id === o.value))
                        : [];

                    const canUpdateReminder = group.status === "reminder" && isAdmin;
                    const canUpdateCompleted = group.status === "completed" && isAdmin;
                    const canDelete = hasPermission("Appointment Vaccine delete") || isAdmin;

                    return (
                        <React.Fragment key={group.group_id}>
                            <Col>
                                <Card className={`h-100 shadow-sm border-0 border-start border-4 rounded-3 bg-white ${group.status === "completed" ? "border-success" : "border-warning"}`}>
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <Badge bg={group.status === "completed" ? "success-subtle" : "warning-subtle"} className={`${group.status === "completed" ? "text-success" : "text-warning-emphasis"} fw-semibold px-2 rounded-pill small`}>
                                                    {group.status === "completed" ? <FaCheck className="me-1" size={10} /> : <FaBell className="me-1" size={10} />}
                                                    {group.status === "completed" ? t("Completed Shot") : t("Reminder Shot")}
                                                </Badge>
                                                <div className="d-flex align-items-center gap-2">
                                                    {!complete?.open && !upd?.open && <span className="text-muted small fw-medium">{moment(group.vaccine_date).format("DD-MMM-YYYY")}</span>}
                                                    {canDelete && !complete?.open && !upd?.open && (
                                                        <Button variant="link" className="text-danger p-0 border-0" onClick={() => handleDelete(group.group_id)}>
                                                            <FaTrash size={12} />
                                                        </Button>
                                                    )}
                                                    {canUpdateCompleted && (
                                                        <Button variant="link" className="text-dark p-0 border-0" onClick={() => openUpdate(group)}>
                                                            <FaEdit size={12} className="me-1" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mb-2 text-dark">
                                                <span className="fw-semibold text-dark-emphasis" style={{ fontSize: "0.95rem" }}>{group.vaccines}</span>
                                            </div>
                                        </div>

                                        {group.status === "completed" ? (
                                            <>
                                                {!upd?.open ? (
                                                    <div className="pt-2 border-top mt-3">
                                                        <div className="d-flex justify-content-between text-muted small">
                                                            {group.temp && <div className="bg-light px-2 py-1 rounded-2"><FaThermometerHalf className="text-info me-1" />{group.temp}°C</div>}
                                                            {group.price !== null && <div className="fw-semibold text-success bg-success-subtle px-2 py-1 rounded-2"><FaMoneyBillWave className="me-1" />{group.price}</div>}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Vaccine Administered Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={upd.vaccine_date} onChange={e => updateUpdateField(group.group_id, "vaccine_date", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Temperature")} (°C)</Form.Label>
                                                            <Form.Control size="sm" type="number" step="0.1" value={upd.temp} onChange={e => updateUpdateField(group.group_id, "temp", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Vaccines")}</Form.Label>
                                                            <Select
                                                                options={vaccineOptions.filter(
                                                                    (option) => Number(option.stock ?? 0) > 0
                                                                )}
                                                                value={currentUpdateSelectedValues}
                                                                onChange={sel => updateUpdateVaccines(group.group_id, sel)}
                                                                formatOptionLabel={formatOptionLabel}
                                                                isMulti
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>

                                                        {upd.rows.map(row => (
                                                            <div key={row.vaccine_id} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                                <div className="small fw-bold pe-3">{row.vaccine_name || findVaccine(row.vaccine_id)?.label}</div>
                                                                <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeUpdateVaccine(group.group_id, row.vaccine_id)}><FaTimes size={12} /></Button>
                                                                <Row g={2} className="mt-1">
                                                                    <Col><Form.Control size="sm" type="number" placeholder="Price" value={row.price} onChange={e => updateUpdateRow(group.group_id, row.vaccine_id, "price", e.target.value)} /></Col>
                                                                    <Col><Form.Control size="sm" type="number" placeholder="Qty" value={row.quantity} onChange={e => updateUpdateRow(group.group_id, row.vaccine_id, "quantity", e.target.value)} /></Col>
                                                                </Row>
                                                            </div>
                                                        ))}

                                                        {!hasPendingVaccines && (
                                                            <div
                                                                className={`d-flex align-items-center rounded px-3 py-2 my-3 ${upd.addReminder
                                                                    ? "bg-warning bg-opacity-25 border border-warning"
                                                                    : "bg-light border"
                                                                    }`}
                                                                style={{
                                                                    cursor: "pointer",
                                                                    transition: "all .2s ease",
                                                                }}
                                                                onClick={() =>
                                                                    updateUpdateField(group.group_id, "addReminder", !upd.addReminder)
                                                                }
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={upd.addReminder || false}
                                                                    className="m-0"
                                                                    onChange={(e) => {
                                                                        e.stopPropagation();
                                                                        updateUpdateField(group.group_id, "addReminder", e.target.checked);
                                                                    }}
                                                                    label=""
                                                                />

                                                                <span
                                                                    className={`ms-2 fw-bold ${upd.addReminder ? "text-danger" : "text-primary"
                                                                        }`}
                                                                    style={{
                                                                        fontSize: "17px",
                                                                        userSelect: "none",
                                                                    }}
                                                                >
                                                                    📅 Schedule Next Vaccine Reminder?
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeUpdate(group.group_id)} disabled={upd.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="primary" size="sm" className="w-50" onClick={() => submitUpdate(group)} disabled={upd.submitting}>
                                                                {upd.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="mt-3 pt-2 border-top">
                                                {!complete?.open && !upd?.open ? (
                                                    <div className="d-flex gap-1.5">
                                                        {canUpdateReminder && (
                                                            <Button variant="primary" size="sm" className="w-50 fw-semibold rounded-3 py-1.5" onClick={() => openUpdate(group)}>
                                                                <FaEdit size={12} className="me-1" /> {t("Update")}
                                                            </Button>
                                                        )}
                                                        {isAppointmentOpen &&
                                                            hasPermission("Appointment Vaccine edit") &&
                                                            !hasMatchedVaccineAppointment && (
                                                                <Button variant="success" size="sm" className={canUpdateReminder ? "w-50 fw-semibold rounded-3 py-1.5" : "w-100 fw-semibold rounded-3 py-1.5"} onClick={() => openComplete(group)}>
                                                                    <FaCheck size={12} className="me-1" /> {t("Complete Shot")}
                                                                </Button>
                                                            )}
                                                    </div>
                                                ) : upd?.open ? (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-3">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Vaccines")}</Form.Label>
                                                            <Select
                                                                options={vaccineOptions}
                                                                value={currentUpdateReminderValues}
                                                                isMulti
                                                                onChange={sel => updateUpdateReminderVaccines(group.group_id, sel)}
                                                                formatOptionLabel={formatOptionLabel}
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={upd.reminder_date} onChange={e => updateUpdateField(group.group_id, "reminder_date", e.target.value)} />
                                                        </Form.Group>

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeUpdate(group.group_id)} disabled={upd.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="primary" size="sm" className="w-50" onClick={() => submitUpdate(group)} disabled={upd.submitting}>
                                                                {upd.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white p-2 border rounded mt-2">
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-1">{t("Vaccine Administered Date")}</Form.Label>
                                                            <Form.Control size="sm" type="date" value={complete.vaccine_date} onChange={e => updateCompleteField(group.group_id, "vaccine_date", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Temperature")} (°C)</Form.Label>
                                                            <Form.Control size="sm" type="number" step="0.1" value={complete.temp} onChange={e => updateCompleteField(group.group_id, "temp", e.target.value)} />
                                                        </Form.Group>
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Vaccines")}</Form.Label>
                                                            <Select
                                                                options={vaccineOptions.filter(
                                                                    (option) => Number(option.stock ?? 0) > 0
                                                                )}
                                                                value={currentSelectedValues}
                                                                onChange={sel => updateCompleteVaccines(group.group_id, sel)}
                                                                formatOptionLabel={formatOptionLabel}
                                                                isMulti
                                                                styles={selectStyles}
                                                                menuPortalTarget={document.body}
                                                            />
                                                        </Form.Group>

                                                        {complete.rows.map(row => (
                                                            <div key={row.vaccine_id} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                                <div className="small fw-bold pe-3">{row.vaccine_name || findVaccine(row.vaccine_id)?.label}</div>
                                                                <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeCompleteVaccine(group.group_id, row.vaccine_id)}><FaTimes size={12} /></Button>
                                                                <Row g={2} className="mt-1">
                                                                    <Col><Form.Control size="sm" type="number" placeholder="Price" value={row.price} onChange={e => updateCompleteRow(group.group_id, row.vaccine_id, "price", e.target.value)} /></Col>
                                                                    <Col><Form.Control size="sm" type="number" placeholder="Qty" value={row.quantity} onChange={e => updateCompleteRow(group.group_id, row.vaccine_id, "quantity", e.target.value)} /></Col>
                                                                </Row>
                                                            </div>
                                                        ))}

                                                        <div
                                                            className={`d-flex align-items-center rounded px-3 py-2 my-3 ${complete.addReminder
                                                                ? "bg-warning bg-opacity-25 border border-warning"
                                                                : "bg-light border"
                                                                }`}
                                                            style={{
                                                                cursor: "pointer",
                                                                transition: "all .2s ease",
                                                            }}
                                                            onClick={() =>
                                                                updateCompleteField(group.group_id, "addReminder", !complete.addReminder)
                                                            }
                                                        >
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={complete.addReminder}
                                                                className="m-0"
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    updateCompleteField(
                                                                        group.group_id,
                                                                        "addReminder",
                                                                        e.target.checked
                                                                    );
                                                                }}
                                                                label=""
                                                            />

                                                            <span
                                                                className={`ms-2 fw-bold ${complete.addReminder ? "text-danger" : "text-primary"
                                                                    }`}
                                                                style={{
                                                                    fontSize: "17px",
                                                                    userSelect: "none",
                                                                }}
                                                            >
                                                                🔔 {t("Schedule Next Vaccine Reminder?")}
                                                            </span>
                                                        </div>

                                                        <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                            <Button variant="secondary" size="sm" className="w-50" onClick={() => closeComplete(group.group_id)} disabled={complete.submitting}>{t("Cancel")}</Button>
                                                            <Button variant="success" size="sm" className="w-50" onClick={() => submitComplete(group)} disabled={complete.submitting}>
                                                                {complete.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {complete?.open && complete.addReminder && (
                                <Col>
                                    <Card className="h-100 shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                        <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                        <FaBell className="me-1" size={10} /> {t("New Reminder Setup")}
                                                    </Badge>
                                                    <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateCompleteField(group.group_id, "addReminder", false)}>
                                                        <FaTimes size={14} />
                                                    </Button>
                                                </div>
                                                <div className="p-1 mt-2">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Next Vaccines")}</Form.Label>
                                                        <Select options={vaccineOptions} formatOptionLabel={formatOptionLabel} isMulti onChange={sel => updateCompleteReminders(group.group_id, sel)} styles={selectStyles} menuPortalTarget={document.body} />
                                                    </Form.Group>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                        <Form.Control size="sm" type="date" value={complete.reminder_date} onChange={e => updateCompleteField(group.group_id, "reminder_date", e.target.value)} />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                                💡 {t("This reminder will save along with the shot validation form.")}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {upd?.open && upd.status === "completed" && upd.addReminder && (
                                <Col>
                                    <Card className="h-100 shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                        <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                            <div>
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                        <FaBell className="me-1" size={10} /> {t("New Reminder Setup")}
                                                    </Badge>
                                                    <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateUpdateField(group.group_id, "addReminder", false)}>
                                                        <FaTimes size={14} />
                                                    </Button>
                                                </div>
                                                <div className="p-1 mt-2">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Next Vaccines")}</Form.Label>
                                                        <Select
                                                            options={vaccineOptions}
                                                            formatOptionLabel={formatOptionLabel}
                                                            isMulti
                                                            onChange={sel => updateUpdateReminderVaccines(group.group_id, sel)}
                                                            styles={selectStyles}
                                                            menuPortalTarget={document.body}
                                                            value={currentUpdateReminderSelectedValues}
                                                        />
                                                    </Form.Group>
                                                    <Form.Group className="mb-2">
                                                        <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="date"
                                                            value={upd.reminder_date || ""}
                                                            onChange={e => updateUpdateField(group.group_id, "reminder_date", e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </div>
                                            <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                                💡 {t("This reminder will save along with the update form.")}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </React.Fragment>
                    );
                })}

                {addShotState.open && (
                    <>
                        <Col>
                            <Card className="h-100 shadow-sm border-0 border-start border-4 border-success rounded-3 bg-white">
                                <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                    <div>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <Badge bg="success-subtle" className="text-success fw-semibold px-2 rounded-pill small">
                                                <FaPlus className="me-1" size={10} /> {t("New Shot Action")}
                                            </Badge>
                                        </div>

                                        <div className="bg-white p-2 border rounded mt-2">
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-1">{t("Vaccine Date")}</Form.Label>
                                                <Form.Control size="sm" type="date" value={addShotState.vaccine_date} onChange={e => updateAddShotStateField("vaccine_date", e.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-0">{t("Temperature")} (°C)</Form.Label>
                                                <Form.Control size="sm" type="number" step="0.1" value={addShotState.temp} onChange={e => updateAddShotStateField("temp", e.target.value)} />
                                            </Form.Group>
                                            <Form.Group className="mb-2">
                                                <Form.Label className="small fw-bold text-secondary mb-0">{t("Select Vaccines")}</Form.Label>
                                                <Select options={vaccineOptions.filter(
                                                    (option) => Number(option.stock ?? 0) > 0
                                                )} formatOptionLabel={formatOptionLabel} value={vaccineOptions.filter(o => addShotState.vaccines.some(r => r.vaccine_id === o.value))} onChange={updateAddShotVaccines} isMulti styles={selectStyles} menuPortalTarget={document.body} />
                                            </Form.Group>

                                            {addShotState.vaccines.map(v => (
                                                <div key={v.vaccine_id} className="bg-light rounded p-2 mb-2 border border-secondary-subtle position-relative">
                                                    <div className="small fw-bold pe-3">{v.vaccine_name}</div>
                                                    <Button variant="link" className="text-danger p-0 position-absolute end-0 top-0 mt-1 me-2" onClick={() => removeAddShotVaccine(v.vaccine_id)}><FaTimes size={12} /></Button>
                                                    <Row g={2} className="mt-1">
                                                        <Col><Form.Control size="sm" type="number" placeholder="Price" value={v.price} onChange={e => updateAddShotRow(v.vaccine_id, "price", e.target.value)} /></Col>
                                                        <Col><Form.Control size="sm" type="number" placeholder="Qty" value={v.quantity} onChange={e => updateAddShotRow(v.vaccine_id, "quantity", e.target.value)} /></Col>
                                                    </Row>
                                                </div>
                                            ))}

                                            <div
                                                className={`d-flex align-items-center rounded px-3 py-2 my-3 ${addShotState.addReminder
                                                    ? "bg-warning bg-opacity-25 border border-warning"
                                                    : "bg-light border"
                                                    }`}
                                                style={{
                                                    cursor: "pointer",
                                                    transition: "all .2s ease",
                                                }}
                                                onClick={() =>
                                                    updateAddShotStateField(
                                                        "addReminder",
                                                        !addShotState.addReminder
                                                    )
                                                }
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={addShotState.addReminder}
                                                    className="m-0"
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        updateAddShotStateField(
                                                            "addReminder",
                                                            e.target.checked
                                                        );
                                                    }}
                                                    label=""
                                                />

                                                <span
                                                    className={`ms-2 fw-bold ${addShotState.addReminder
                                                        ? "text-danger"
                                                        : "text-primary"
                                                        }`}
                                                    style={{
                                                        fontSize: "17px",
                                                        userSelect: "none",
                                                    }}
                                                >
                                                    🔔 {t("Schedule Next Vaccine Reminder?")}
                                                </span>
                                            </div>

                                            <div className="d-flex gap-1.5 mt-2 pt-2 border-top">
                                                <Button variant="secondary" size="sm" className="w-50" onClick={closeAddShot} disabled={addShotState.submitting}>{t("Cancel")}</Button>
                                                <Button variant="success" size="sm" className="w-50" onClick={submitAddShot} disabled={addShotState.submitting}>
                                                    {addShotState.submitting ? <Spinner animation="border" size="sm" /> : t("Save")}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {addShotState.addReminder && (
                            <Col>
                                <Card className="h-100 shadow-sm border-0 border-start border-4 border-info rounded-3 bg-white">
                                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <Badge bg="info-subtle" className="text-info-emphasis fw-semibold px-2 rounded-pill small">
                                                    <FaBell className="me-1" size={10} /> {t("New Reminder Setup")}
                                                </Badge>
                                                <Button variant="link" className="text-muted p-0 m-0" onClick={() => updateAddShotStateField("addReminder", false)}>
                                                    <FaTimes size={14} />
                                                </Button>
                                            </div>
                                            <div className="p-1 mt-2">
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="small fw-bold text-secondary mb-1">{t("Select Next Vaccines")}</Form.Label>
                                                    <Select options={vaccineOptions} formatOptionLabel={formatOptionLabel} isMulti onChange={updateAddShotReminders} styles={selectStyles} menuPortalTarget={document.body} />
                                                </Form.Group>
                                                <Form.Group className="mb-2">
                                                    <Form.Label className="small fw-bold text-secondary mb-1">{t("Reminder Date")}</Form.Label>
                                                    <Form.Control size="sm" type="date" value={addShotState.reminder_date} onChange={e => updateAddShotStateField("reminder_date", e.target.value)} />
                                                </Form.Group>
                                            </div>
                                        </div>
                                        <div className="text-muted small bg-light p-2 rounded border border-dashed mt-3">
                                            💡 {t("This reminder will save along with the new shot creation form.")}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )}
                    </>
                )}

                {(isAdmin || (showInlineAddButton && !hasMatchedVaccineAppointment)) && !addShotState.open && (
                    <Col>
                        <Card
                            onClick={openAddShot}
                            className="h-100 shadow-sm border-2 border-dashed rounded-3 bg-white d-flex align-items-center justify-content-center text-center cursor-pointer p-4 transition-all hover-bg-light"
                            style={{ minHeight: "220px", cursor: "pointer", borderColor: "#cbd5e1" }}
                        >
                            <div className="text-muted">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ width: "45px", height: "45px" }}>
                                    <FaPlus size={18} />
                                </div>
                                <h6 className="fw-bold text-dark-emphasis mb-1">{t("Add Shot")}</h6>
                                <p className="small mb-0 opacity-75">{groups.length === 0 ? t("Configure first entry") : t("Configure next setup sequence")}</p>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );
}